import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import {
  Form,
  FormButtons,
  FormField,
  FormProps,
  FormSchemaEntry,
  IRecipientAddressFormValues,
  RecipientAddressForm,
  recipientAddressFormSchema,
  ValidationError,
} from '@gik/ui';
import { FormRenderProps } from 'react-final-form';
import i18n from '@gik/i18n';
import { translationKeys } from '@gik/create/i18n/en';

export interface IRecipientInfoAddressFormProps extends Omit<FormProps, 'onSubmit' | 'onChange'> {
  buttons?: (form: FormRenderProps, formId: string) => React.ReactNode;
  buttonsPortal?: () => HTMLElement;
  onSubmit?: (values: IRecipientInfoAddressFormValues, form: FormRenderProps) => void;
  onChange?: (values: IRecipientInfoAddressFormValues, form: FormRenderProps) => void;
  form?: FormRenderProps;
  values?: IRecipientInfoAddressFormValues;
  isMyself: boolean;
}

export interface IRecipientInfoAddressFormValues extends IRecipientAddressFormValues {
  includeRecipientAddress: boolean;
}

export interface IRecipientInfoAddressFormErrors {
  includeRecipientAddress?: ValidationError;
}

export enum recipientInfoAddressInputNames {
  IncludeRecipientAddress = 'includeRecipientAddress',
}

export const recipientAddressStepFormSchema = (isMyself: boolean): FormSchemaEntry[] => [
  {
    type: 'checkbox',
    name: recipientInfoAddressInputNames.IncludeRecipientAddress,
    props: {
      label: (
        <div>
          <span>
            {i18n.t(isMyself ? translationKeys.IncludeYourAddressLabel : translationKeys.IncludeRecipientAddressLabel)}
          </span>
          <br />
          <span>{i18n.t(translationKeys.IncludeRecipientAddressSublabel)}</span>
        </div>
      ),
    },
  },
];

export const recipientInfoAddressFormSchema = (isMyself: boolean): FormSchemaEntry[] =>
  recipientAddressStepFormSchema(isMyself).concat(recipientAddressFormSchema(true));

const blockName = 'recipient-info-address-form';

export const formId = 'CreateRecipientInfoStepForm';

function RecipientInfoAddressForm(
  {
    className,
    form,
    onSubmit,
    onChange,
    values,
    buttons,
    buttonsPortal,
    isMyself,
    ...otherProps
  }: IRecipientInfoAddressFormProps,
  ref: React.Ref<unknown>
) {
  const bem = bemBlock(blockName);

  const [_values, _setValues] = React.useState<IRecipientInfoAddressFormValues>();

  let formRenderProps: FormRenderProps;
  let submitForm: Function;

  React.useImperativeHandle(ref, () => ({
    submit(event) {
      submitForm(event);
    },
  }));

  function handleSubmit(values: IRecipientInfoAddressFormValues) {
    if (onSubmit) {
      onSubmit(values, formRenderProps);
    }
  }

  function handleChange(values: IRecipientInfoAddressFormValues) {
    _setValues(values);
    if (onChange) {
      onChange(values, formRenderProps);
    }
  }

  const fields = (form: FormRenderProps, values: IRecipientInfoAddressFormValues) => {
    return (
      <div className="gik-form">
        <FormField name={recipientInfoAddressInputNames.IncludeRecipientAddress} inline />

        {values?.includeRecipientAddress && <RecipientAddressForm form={form} required={false} />}

        <FormButtons buttons={buttons} buttonsPortal={buttonsPortal} form={form} formId={formId} />
      </div>
    );
  };

  if (form) {
    return fields(form, values);
  }

  return (
    <Form
      className={bem(null, null, [className, 'gik-form--white'])}
      schema={recipientAddressStepFormSchema(isMyself)}
      onSubmit={handleSubmit}
      onChange={handleChange}
      vertical
      id={formId}
      render={(form: FormRenderProps) => {
        submitForm = form.handleSubmit;
        return fields(form, _values);
      }}
      {...otherProps}
    />
  );
}

export default React.forwardRef(RecipientInfoAddressForm);
